/**
 * Hook para gerenciamento do Feed Global de Atividades
 * Busca todas as atividades do tenant com suporte a filtros avançados
 * Implementa scroll infinito com cursor composto (created_at + id)
 * 
 * @version 2.0.0
 * RELEASE: 2026-02-09 v1.2.0 - Adicionado suporte a filtros: workspace, seller, user, status, período
 * RELEASE: 2026-02-09 v1.1.0 - Mutation para reabrir atividade concluída
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 * RELEASE: 2026-02-10 v2.0.0 - Scroll infinito com useInfiniteQuery + cursor composto
 */
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useMemo } from 'react';
import type { ActivityType } from '@/hooks/useCrmDealActivities';
import type { ActivityStatusFilter } from '@/components/crm/filters/CrmActivityStatusFilter';
import type { ActivityOriginFilter } from '@/components/crm/filters/CrmActivityOriginFilter';

// Logger do módulo
const logger = createLogger('ActivitiesFeed');

/** Tamanho da página para scroll infinito */
const PAGE_SIZE = 30;

/**
 * Interface de anexo da atividade
 */
export interface FeedActivityAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number | null;
  public_url: string;
  thumbnail_url: string | null;
  mime_type: string | null;
}

/**
 * Interface da atividade do feed
 */
export interface FeedActivity {
  id: string;
  activity_type: string;
  title: string;
  description: string | null;
  scheduled_at: string | null;
  completed_at: string | null;
  is_system: boolean;
  /** v1.11.0: marca "lida" do usuário atual (notas/sistema) */
  is_read?: boolean;
  created_at: string;
  deal: {
    id: string;
    title: string;
  } | null;
  customer: {
    id: string;
    nome: string | null;
  } | null;
  performed_by_user: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  attachments?: FeedActivityAttachment[];
  comments_count?: number;
  /** Link de videoconferência (Google Meet) vindo do scheduling_bookings */
  conferencing_link?: string | null;
}

/**
 * Interface dos filtros de atividades (v1.2.0)
 */
export interface ActivitiesFeedFilters {
  /** Tipo de atividade (controlado pelas abas) */
  activityType?: ActivityType;
  /** Somente com comentários (filtro client-side legado, mantido por compat) */
  onlyWithComments?: boolean;
  /** v1.10.0: Somente atividades em que o usuário corrente foi mencionado (server-side) */
  onlyWithMyMentions?: boolean;
  /** Data específica do calendário */
  selectedDate?: Date;
  /** Workspace do negócio (null = todos) */
  workspaceId?: string | null;
  /** Vendedor do negócio (null = todos) */
  sellerId?: string | null;
  /** Responsável pela atividade (null = todos) */
  performedByUserId?: string | null;
  /** Status: 'all', 'open', 'completed' */
  status?: ActivityStatusFilter;
  /** Origem: 'all', 'manual', 'system' - v1.7.2 */
  origin?: ActivityOriginFilter;
  /** Data inicial do período */
  startDate?: Date;
  /** Data final do período */
  endDate?: Date;
}

/**
 * Opções do hook
 */
interface ActivitiesFeedOptions {
  enabled?: boolean;
  filters?: ActivitiesFeedFilters;
}

/**
 * Cursor composto para paginação server-side
 * Usa created_at (effective_ts) + id para estabilidade
 */
interface CursorParam {
  cursor_created_at: string;
  cursor_id: string;
}

/**
 * Hook para o feed global de atividades do CRM
 * v2.0.0: Usa useInfiniteQuery com cursor composto para scroll infinito
 */
export function useActivitiesFeed(options?: ActivitiesFeedOptions) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const filters = options?.filters ?? {};

  // Query infinita para buscar atividades com paginação server-side
  const infiniteQuery = useInfiniteQuery({
    queryKey: ['crm-activities-feed', currentTenant?.id, filters],
    queryFn: async ({ pageParam }) => {
      if (!currentTenant?.id) return [];

      const cursor = pageParam as CursorParam | null;
      logger.debug('Buscando página do feed via RPC:', { filters, cursor });

      // Formata datas para o formato esperado pelo RPC (YYYY-MM-DD)
      const selectedDateStr = filters.selectedDate 
        ? format(filters.selectedDate, 'yyyy-MM-dd')
        : null;
      const startDateStr = filters.startDate 
        ? format(filters.startDate, 'yyyy-MM-dd')
        : null;
      const endDateStr = filters.endDate 
        ? format(filters.endDate, 'yyyy-MM-dd')
        : null;

      // Chama RPC com filtros de segurança + cursor v1.4.0
      // v1.7.3: Passa timezone local para filtros de data corretos
      const { data, error } = await supabase.rpc('get_user_activities_feed', {
        p_activity_type: filters.activityType || null,
        p_selected_date: selectedDateStr,
        p_limit: PAGE_SIZE,
        p_tenant_hint: currentTenant.id,
        p_workspace_id: filters.workspaceId || null,
        p_seller_id: filters.sellerId || null,
        p_user_id: filters.performedByUserId || null,
        // v2.1.0 — Quando filtra a aba "Nota", ignora status (notas não têm ciclo de conclusão)
        p_status: filters.activityType === 'note'
          ? null
          : (filters.status === 'all' ? null : (filters.status || null)),
        p_start_date: startDateStr,
        p_end_date: endDateStr,
        p_filter_by_scheduled: !!selectedDateStr,
        // v1.7.2: Filtro de origem (Manual/Sistema)
        p_is_system: filters.origin === 'manual' ? false : filters.origin === 'system' ? true : null,
        // v2.0.0: Parâmetros de cursor composto
        p_cursor_created_at: cursor?.cursor_created_at || null,
        p_cursor_id: cursor?.cursor_id || null,
        // v1.7.3: Timezone do navegador para cast correto de datas
        p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // v1.10.0: filtro server-side da aba "Comentários" (apenas menções ao usuário)
        p_only_with_my_mentions: !!filters.onlyWithMyMentions,
      });

      if (error) {
        logger.error('Erro ao buscar atividades do feed via RPC:', error.message);
        throw error;
      }

      // Transforma resultado flat em estrutura aninhada compatível com FeedActivity
      // v1.8.0: conferencing_link retornado diretamente pela RPC
      // v1.9.0: attachments retornados diretamente pela RPC (elimina select extra)
      type FeedRow = {
        id: string;
        activity_type: string;
        title: string;
        description: string | null;
        scheduled_at: string | null;
        completed_at: string | null;
        is_system: boolean;
        created_at: string;
        deal_id: string | null;
        deal_title: string | null;
        customer_id: string | null;
        customer_nome: string | null;
        performed_by_id: string | null;
        performed_by_name: string | null;
        performed_by_avatar: string | null;
        comments_count: number;
        conferencing_link: string | null;
        attachments: FeedActivityAttachment[] | null;
        is_read: boolean | null;
      };

      const activities: FeedActivity[] = ((data || []) as unknown as FeedRow[]).map((row) => ({
        id: row.id,
        activity_type: row.activity_type,
        title: row.title,
        description: row.description,
        scheduled_at: row.scheduled_at,
        completed_at: row.completed_at,
        is_system: row.is_system,
        created_at: row.created_at,
        deal: row.deal_id ? { id: row.deal_id, title: row.deal_title || '' } : null,
        customer: row.customer_id ? { id: row.customer_id, nome: row.customer_nome } : null,
        performed_by_user: row.performed_by_id ? {
          id: row.performed_by_id,
          full_name: row.performed_by_name,
          avatar_url: row.performed_by_avatar,
        } : null,
        comments_count: row.comments_count || 0,
        conferencing_link: row.conferencing_link || null,
        // v1.9.0: attachments inline da RPC (elimina select direto a crm_activity_attachments)
        attachments: Array.isArray(row.attachments) ? row.attachments : [],
        // v1.11.0: marca "lida" do usuário atual
        is_read: !!row.is_read,
      }));

      logger.debug(`Página carregada: ${activities.length} atividades`);
      return activities;
    },
    initialPageParam: null as CursorParam | null,
    getNextPageParam: (lastPage) => {
      // Se retornou menos que PAGE_SIZE, não há mais páginas
      if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
      // Cursor composto: created_at + id do último item
      const lastItem = lastPage[lastPage.length - 1];
      return {
        cursor_created_at: lastItem.created_at,
        cursor_id: lastItem.id,
      } as CursorParam;
    },
    enabled: options?.enabled !== false && !!currentTenant?.id,
    staleTime: 30 * 1000,
  });

  // Flatten de todas as páginas em array único para compatibilidade
  const allActivities = useMemo(() => {
    const flat = infiniteQuery.data?.pages.flat() || [];

    // Aplica filtro de "somente com comentários" pós-RPC
    if (filters.onlyWithComments) {
      return flat.filter(a => (a.comments_count || 0) > 0);
    }
    return flat;
  }, [infiniteQuery.data?.pages, filters.onlyWithComments]);

  // Contadores progressivos (sobre itens carregados)
  // v2.1.0 — Notas não participam do ciclo pendente/concluída
  const pendingCount = useMemo(() => 
    allActivities.filter(a => a.activity_type !== 'note' && a.scheduled_at && !a.completed_at).length,
    [allActivities]
  );
  const completedCount = useMemo(() => 
    allActivities.filter(a => a.activity_type !== 'note' && a.completed_at !== null).length,
    [allActivities]
  );
  const activitiesWithComments = useMemo(() => 
    allActivities.filter(a => (a.comments_count || 0) > 0).length,
    [allActivities]
  );

  /**
   * Função auxiliar para invalidar todos os caches relacionados à página
   * Garante sincronização entre feed, KPIs e contadores
   */
  const invalidateAllCaches = () => {
    // Invalida feed de atividades (todas as páginas do infinite query)
    queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
    // Invalida KPIs do Balance Card
    queryClient.invalidateQueries({ queryKey: ['activities-balance-kpi'] });
    // Invalida contadores das abas
    queryClient.invalidateQueries({ queryKey: ['activities-feed-counts'] });
    // Invalida menções não lidas
    queryClient.invalidateQueries({ queryKey: ['unread-mentions-count'] });
  };

  // Mutation para marcar atividade como concluída
  const completeActivity = useMutation({
    mutationFn: async (activityId: string) => {
      logger.debug('Marcando atividade como concluída:', activityId);

      const { data, error } = await supabase
        .from('crm_deal_activities')
        .update({ completed_at: new Date().toISOString() })
        .eq('id', activityId)
        .select()
        .single();

      if (error) {
        logger.error('Erro ao concluir atividade:', error.message);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      invalidateAllCaches();
      toast.success('Atividade concluída!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao concluir atividade:', error.message);
      toast.error(`Erro ao concluir atividade: ${error.message}`);
    },
  });

  // RELEASE: 2026-02-09 v1.1.0 - Mutation para reabrir atividade concluída
  const reopenActivity = useMutation({
    mutationFn: async (activityId: string) => {
      logger.debug('Reabrindo atividade:', activityId);

      const { data, error } = await supabase
        .from('crm_deal_activities')
        .update({ completed_at: null })
        .eq('id', activityId)
        .select()
        .single();

      if (error) {
        logger.error('Erro ao reabrir atividade:', error.message);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      invalidateAllCaches();
      toast.success('Atividade reaberta!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao reabrir atividade:', error.message);
      toast.error(`Erro ao reabrir atividade: ${error.message}`);
    },
  });

  // Mutation para deletar atividade
  const deleteActivity = useMutation({
    mutationFn: async (activityId: string) => {
      logger.debug('Removendo atividade:', activityId);

      const { error } = await supabase
        .from('crm_deal_activities')
        .delete()
        .eq('id', activityId)
        .eq('is_system', false);

      if (error) {
        logger.error('Erro ao remover atividade:', error.message);
        throw error;
      }
    },
    onSuccess: () => {
      invalidateAllCaches();
      toast.success('Atividade removida!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao remover atividade:', error.message);
      toast.error(`Erro ao remover atividade: ${error.message}`);
    },
  });

  // ===========================================================================
  // RELEASE: 2026-05-14 v1.11.0 — Mutations para "Lido" (Notas e Sistema)
  // ===========================================================================

  /** Marca atividade individual (nota/sistema) como lida pelo usuário atual */
  const markActivityRead = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('mark_activity_read', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) {
        logger.error('Erro ao marcar como lido:', error.message);
        throw error;
      }
    },
    onSuccess: () => invalidateAllCaches(),
    onError: (error: Error) => {
      toast.error(`Erro ao marcar como lido: ${error.message}`);
    },
  });

  /** Remove a marca "lida" do usuário atual */
  const markActivityUnread = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('mark_activity_unread', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) {
        logger.error('Erro ao desmarcar leitura:', error.message);
        throw error;
      }
    },
    onSuccess: () => invalidateAllCaches(),
    onError: (error: Error) => {
      toast.error(`Erro ao desmarcar leitura: ${error.message}`);
    },
  });

  /** Marca todas as notas/sistema visíveis (com filtros) como lidas */
  const markAllRead = useMutation({
    mutationFn: async (params: {
      scope: 'note' | 'system';
      workspaceId?: string | null;
      sellerId?: string | null;
      performedByUserId?: string | null;
      startDate?: Date | null;
      endDate?: Date | null;
    }) => {
      const startDateStr = params.startDate
        ? format(params.startDate, 'yyyy-MM-dd')
        : null;
      const endDateStr = params.endDate
        ? format(params.endDate, 'yyyy-MM-dd')
        : null;

      const { data, error } = await supabase.rpc('mark_all_activities_read', {
        p_scope: params.scope,
        p_workspace_id: params.workspaceId ?? null,
        p_seller_id: params.sellerId ?? null,
        p_user_id: params.performedByUserId ?? null,
        p_start_date: startDateStr,
        p_end_date: endDateStr,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) {
        logger.error('Erro ao marcar todos como lidos:', error.message);
        throw error;
      }
      return (data as number) ?? 0;
    },
    onSuccess: (count) => {
      invalidateAllCaches();
      toast.success(`${count} atividade(s) marcada(s) como lida(s)`);
    },
    onError: (error: Error) => {
      toast.error(`Erro ao marcar todos como lidos: ${error.message}`);
    },
  });

  /**
   * Função para atualizar TODOS os dados da página silenciosamente
   * Usada pelo botão de refresh - invalida todas as páginas do infinite query
   */
  const refreshAll = async () => {
    logger.debug('Atualizando todos os dados da página...');
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] }),
      queryClient.invalidateQueries({ queryKey: ['activities-balance-kpi'] }),
      queryClient.invalidateQueries({ queryKey: ['activities-feed-counts'] }),
      queryClient.invalidateQueries({ queryKey: ['unread-mentions-count'] }),
    ]);
  };

  return {
    activities: allActivities,
    activitiesWithComments,
    pendingCount,
    completedCount,
    isLoading: infiniteQuery.isLoading,
    isRefetching: infiniteQuery.isRefetching,
    error: infiniteQuery.error,
    refetch: infiniteQuery.refetch,
    refreshAll,
    completeActivity,
    reopenActivity,
    deleteActivity,
    // v1.11.0: marca "Lido" para Notas/Sistema
    markActivityRead,
    markActivityUnread,
    markAllRead,
    // v2.0.0: Novas propriedades para scroll infinito
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
  };
}
